import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { createRoot } from "react-dom/client"
import { ICONS } from "../../constants/icons"

export const IMPRESSION_CELEBRATION_EVENT = "amok:impression-react"

const DURATION_MS = 2000

let hostReady = false
let listenerReady = false
let pendingType = null

const normalizeType = type => (type === "hearted" ? "hearted" : "liked")

const dispatchCelebration = kind => {
  window.dispatchEvent(
    new CustomEvent(IMPRESSION_CELEBRATION_EVENT, { detail: { type: kind } }),
  )
}

/**
 * Lightweight like / heart pop when reacting on a session collaborator.
 * @param {'liked'|'hearted'} type
 */
export const celebrateImpression = (type = "liked") => {
  if (typeof window === "undefined" || typeof document === "undefined") return
  const kind = normalizeType(type)
  ensureHost()

  // First call mounts the host asynchronously — queue until the listener is attached
  // so the first like/heart still plays.
  if (!listenerReady) {
    pendingType = kind
    return
  }

  dispatchCelebration(kind)
}

const ensureHost = () => {
  if (hostReady) return
  let node = document.getElementById("amok-impression-celebration-root")
  if (!node) {
    node = document.createElement("div")
    node.id = "amok-impression-celebration-root"
    document.body.appendChild(node)
  }
  createRoot(node).render(<ImpressionCelebrationHost />)
  hostReady = true
}

const ImpressionCelebrationHost = () => {
  const [visible, setVisible] = useState(false)
  const [burstKey, setBurstKey] = useState(0)
  const [type, setType] = useState("liked")

  useEffect(() => {
    const onCelebrate = event => {
      const nextType = normalizeType(event?.detail?.type)
      setType(nextType)
      setBurstKey(k => k + 1)
      setVisible(true)
    }

    window.addEventListener(IMPRESSION_CELEBRATION_EVENT, onCelebrate)
    listenerReady = true

    if (pendingType) {
      const kind = pendingType
      pendingType = null
      onCelebrate({ detail: { type: kind } })
    }

    return () => {
      window.removeEventListener(IMPRESSION_CELEBRATION_EVENT, onCelebrate)
      listenerReady = false
    }
  }, [])

  useEffect(() => {
    if (!visible) return undefined
    const timer = window.setTimeout(() => setVisible(false), DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [visible, burstKey])

  if (!visible || typeof document === "undefined") return null

  const isHeart = type === "hearted"
  const iconSrc = isHeart ? ICONS.heartFill : ICONS.likeFill
  const label = isHeart ? "Hearted" : "Liked"

  return createPortal(
    <div
      key={burstKey}
      className={`impression-celebration impression-celebration--${type}`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="impression-celebration__burst" aria-hidden="true">
        <img
          className="impression-celebration__icon"
          src={iconSrc}
          alt=""
          width={40}
          height={40}
        />
      </div>
    </div>,
    document.body,
  )
}

export default ImpressionCelebrationHost
